/**
 * Chemins d'API et de navigation partagés entre l'API et le frontend.
 * Référence : document 06 § 2.1 (URI de base et versionnage).
 */
export const API_VERSION = 'v1';
export const API_BASE_PATH = `/api/${API_VERSION}`;
export const AUTH_ROUTES = {
    registerCompany: '/auth/register-company',
    registerInvitation: '/auth/register-invitation',
    invitationPreview: '/auth/invitation-preview',
    login: '/auth/login',
    refresh: '/auth/refresh',
    logout: '/auth/logout',
    forgotPassword: '/auth/forgot-password',
    resetPassword: '/auth/reset-password',
    verifyEmail: '/auth/verify-email',
    me: '/auth/me',
    switchTenant: '/auth/switch-tenant',
};
/** Vérification en deux étapes (document 06 § 3.2). */
export const MFA_ROUTES = {
    setup: '/auth/mfa/setup',
    verify: '/auth/mfa/verify',
    disable: '/auth/mfa/disable',
    backupCodes: '/auth/mfa/backup-codes',
};
export const SYSTEM_ROUTES = {
    health: '/health',
    version: '/version',
};
/** Référentiel des rôles et permissions (document 06 § 6.1). */
export const ROLE_ROUTES = {
    list: '/roles',
    create: '/roles',
    detail: '/roles/:id',
};
export const PERMISSION_ROUTES = {
    list: '/permissions',
};
/** Fiche société, paramètres et modules activés (document 06 § 6.2). */
export const TENANT_ROUTES = {
    detail: '/tenant',
    settings: '/tenant/settings',
    featureFlags: '/tenant/feature-flags',
    /** Logo de la société active : dépôt (`POST`), lecture (`GET`), retrait (`DELETE`). */
    logo: '/tenant/logo',
    /** Attestation d'assurance : dépôt (`POST`), lecture (`GET`), retrait (`DELETE`). */
    insuranceDocument: '/tenant/insurance-document',
    /** Identification de la banque à partir du seul code banque (`GET ?bankCode=`). */
    bankLookup: '/tenant/bank-lookup',
    /** Envoi d'un e-mail d'essai, pour valider la configuration et l'aperçu de la signature. */
    emailTest: '/tenant/email/test',
    /** Aperçu de la signature apposée aux e-mails sortants, rendue par le serveur (`GET`). */
    emailSignature: '/tenant/email/signature',
    /** Contexte legal public d'une societe, resolu par son slug. */
    publicLegal: '/public/tenants/:slug/legal',
};
/** Règles de numérotation paramétrables par société (document 06 § 6.2). */
export const NUMBERING_ROUTES = {
    list: '/settings/numbering-sequences',
    create: '/settings/numbering-sequences',
    detail: '/settings/numbering-sequences/:id',
    preview: '/settings/numbering-sequences/preview',
};
/** Adhésions des utilisateurs à la société active (document 06 § 6.3). */
export const USER_ROUTES = {
    list: '/users',
    /** Profil de l'utilisateur authentifié : aucune permission requise. */
    me: '/users/me',
    detail: '/users/:id',
    /** Suppression définitive métier du compte porté par une adhésion. */
    account: '/users/:id/account',
};
/** Invitations à rejoindre une société (document 06 § 6.3). */
export const INVITATION_ROUTES = {
    list: '/invitations',
    create: '/invitations',
    /** Acceptation par l'invité authentifié : hors périmètre de société. */
    accept: '/invitations/accept',
    resend: '/invitations/:id/resend',
    detail: '/invitations/:id',
};
/** Premier incrément CRM : prospects (document 06 § 6.3, à compléter). */
export const PROSPECT_ROUTES = {
    list: '/prospects',
    create: '/prospects',
    convert: '/prospects/:id/convert',
    detail: '/prospects/:id',
};
/** Deuxième incrément CRM : clients. */
export const CLIENT_ROUTES = {
    list: '/clients',
    create: '/clients',
    detail: '/clients/:id',
};
/** Troisième incrément CRM : opportunités commerciales. */
export const OPPORTUNITY_ROUTES = {
    list: '/opportunities',
    create: '/opportunities',
    detail: '/opportunities/:id',
    /** Déplacement d'une opportunité sur le pipeline (kanban). */
    move: '/opportunities/:id/stage',
};
/** Tableau kanban du pipeline commercial. */
export const PIPELINE_ROUTES = {
    board: '/pipeline/board',
};
/** Troisième incrément CRM : activités consignées (E08-07). */
export const ACTIVITY_ROUTES = {
    list: '/activities',
    create: '/activities',
    detail: '/activities/:id',
};
/** Troisième incrément CRM : tâches et rappels (E08-08). */
export const TASK_ROUTES = {
    list: '/tasks',
    create: '/tasks',
    detail: '/tasks/:id',
    /** Évolution du statut d'une tâche et de son horodatage de clôture. */
    status: '/tasks/:id/status',
};
/** Module agenda (E26) : événements, participants et rappels. */
export const CALENDAR_ROUTES = {
    events: '/calendar/events',
    create: '/calendar/events',
    /** Export iCalendar (`.ics`) des occurrences d'une période bornée. */
    export: '/calendar/events/export',
    detail: '/calendar/events/:id',
    /** Évolution du statut d'un événement (confirmé, provisoire, annulé). */
    status: '/calendar/events/:id/status',
    /** Recherche des rendez-vous déjà planifiés sur un créneau pour des utilisateurs internes. */
    conflicts: '/calendar/events/conflicts',
    /** Modèles nommés de confirmation de rendez-vous, proposés à la saisie d'un RDV. */
    confirmationTemplates: '/calendar/confirmation-templates',
    /** Connexions d'agenda externe (US-E26-06) : listing et création. */
    syncConnections: '/calendar/sync-connections',
    /** Cibles de publication Google visibles pour la saisie d'un RDV. */
    syncPublishTargets: '/calendar/sync-connections/publish-targets',
    /**
     * Lancement du parcours OAuth Google. Déclaré avant le détail afin que « google »
     * ne soit pas capturé par le paramètre « :id ».
     */
    googleAuthorize: '/calendar/sync-connections/google/authorize',
    /** Retour du parcours OAuth Google (URI de redirection déclarée côté Cloud Console). */
    googleCallback: '/calendar/sync-connections/google/callback',
    /** Détail d'une connexion : mise à jour et révocation. */
    syncConnectionDetail: '/calendar/sync-connections/:id',
    /** Déclenchement manuel d'une synchronisation de la connexion. */
    syncConnectionSync: '/calendar/sync-connections/:id/sync',
};
/** Aides de saisie CRM : annuaire d'entreprise, adresses, affectations et référentiels métier. */
export const CRM_LOOKUP_ROUTES = {
    companies: '/crm/lookups/companies',
    addresses: '/crm/lookups/addresses',
    assignmentUsers: '/crm/lookups/assignment-users',
    directories: '/crm/lookups/directories',
};
/** Catalogue — articles (E09). */
export const ITEM_ROUTES = {
    list: '/items',
    create: '/items',
    detail: '/items/:id',
};
/** Catalogue — fichiers rattachés aux articles (photos, docs, conditions fournisseur). */
export const ITEM_DOCUMENT_ROUTES = {
    list: '/items/:itemId/documents',
    create: '/items/:itemId/documents',
    detail: '/items/:itemId/documents/:documentId',
    download: '/items/:itemId/documents/:documentId/download',
};
/** Catalogue — catégories d'articles (E09-02). */
export const ITEM_CATEGORY_ROUTES = {
    list: '/item-categories',
    create: '/item-categories',
    detail: '/item-categories/:id',
};
/** Catalogue — référentiels (unités, TVA, modes et conditions de règlement). */
export const UNIT_ROUTES = {
    list: '/units',
    create: '/units',
    detail: '/units/:id',
};
export const VAT_RATE_ROUTES = {
    list: '/vat-rates',
    create: '/vat-rates',
    detail: '/vat-rates/:id',
};
export const PAYMENT_METHOD_ROUTES = {
    list: '/payment-methods',
    create: '/payment-methods',
    detail: '/payment-methods/:id',
};
export const PAYMENT_TERM_ROUTES = {
    list: '/payment-terms',
    create: '/payment-terms',
    detail: '/payment-terms/:id',
};
/** Catalogue — blocs de texte réutilisables (E10). */
export const TEXT_BLOCK_ROUTES = {
    list: '/text-blocks',
    create: '/text-blocks',
    detail: '/text-blocks/:id',
};
/** Catalogue — fournisseurs (E09-03). */
export const SUPPLIER_ROUTES = {
    list: '/suppliers',
    create: '/suppliers',
    detail: '/suppliers/:id',
};
/** Catalogue — conditions d'achat d'un article chez un fournisseur (E09-03). */
export const ITEM_SUPPLIER_ROUTES = {
    list: '/items/:itemId/suppliers',
    create: '/items/:itemId/suppliers',
    detail: '/items/:itemId/suppliers/:id',
};
/** Ventes — devis (E11). */
export const QUOTE_ROUTES = {
    list: '/quotes',
    create: '/quotes',
    detail: '/quotes/:id',
    /** Transition d'état du devis (envoi, consultation, acceptation, refus, annulation). */
    status: '/quotes/:id/status',
};
/** Chemin concret d'un rôle, à partir de son identifiant. */
export function rolePath(id) {
    return ROLE_ROUTES.detail.replace(':id', id);
}
/** Chemin concret d'une adhésion, à partir de l'identifiant de celle-ci. */
export function userPath(id) {
    return USER_ROUTES.detail.replace(':id', id);
}
/** Chemin concret de suppression définitive d'un compte, depuis l'identifiant de son adhésion. */
export function userAccountPath(id) {
    return USER_ROUTES.account.replace(':id', id);
}
/** Chemin concret d'une invitation, à partir de son identifiant. */
export function invitationPath(id) {
    return INVITATION_ROUTES.detail.replace(':id', id);
}
/** Chemin concret de renvoi d'une invitation, à partir de son identifiant. */
export function invitationResendPath(id) {
    return INVITATION_ROUTES.resend.replace(':id', id);
}
/** Chemin concret d'un prospect, à partir de son identifiant. */
export function prospectPath(id) {
    return PROSPECT_ROUTES.detail.replace(':id', id);
}
/** Chemin concret de transformation d'un prospect vers un client. */
export function prospectConvertPath(id) {
    return PROSPECT_ROUTES.convert.replace(':id', id);
}
/** Chemin concret d'un client, à partir de son identifiant. */
export function clientPath(id) {
    return CLIENT_ROUTES.detail.replace(':id', id);
}
/** Chemin concret d'une opportunité, à partir de son identifiant. */
export function opportunityPath(id) {
    return OPPORTUNITY_ROUTES.detail.replace(':id', id);
}
/** Chemin concret de déplacement d'une opportunité sur le pipeline. */
export function opportunityMovePath(id) {
    return OPPORTUNITY_ROUTES.move.replace(':id', id);
}
/** Chemin concret d'une activité CRM, à partir de son identifiant. */
export function activityPath(id) {
    return ACTIVITY_ROUTES.detail.replace(':id', id);
}
/** Chemin concret d'une tâche CRM, à partir de son identifiant. */
export function taskPath(id) {
    return TASK_ROUTES.detail.replace(':id', id);
}
/** Chemin concret d'un événement d'agenda, à partir de son identifiant. */
export function calendarEventPath(id) {
    return CALENDAR_ROUTES.detail.replace(':id', id);
}
/** Chemin concret d'évolution du statut d'un événement d'agenda. */
export function calendarEventStatusPath(id) {
    return CALENDAR_ROUTES.status.replace(':id', id);
}
/** Chemin concret d'une connexion d'agenda externe, à partir de son identifiant. */
export function calendarSyncConnectionPath(id) {
    return CALENDAR_ROUTES.syncConnectionDetail.replace(':id', id);
}
/** Chemin concret de déclenchement manuel d'une synchronisation d'agenda externe. */
export function calendarSyncConnectionSyncPath(id) {
    return CALENDAR_ROUTES.syncConnectionSync.replace(':id', id);
}
/** Chemin concret des cibles de publication visibles pour un RDV. */
export function calendarSyncPublishTargetsPath() {
    return CALENDAR_ROUTES.syncPublishTargets;
}
/** Chemin concret d'un article du catalogue, à partir de son identifiant. */
export function itemPath(id) {
    return ITEM_ROUTES.detail.replace(':id', id);
}
/** Chemin concret des fichiers rattachés à un article. */
export function itemDocumentsPath(itemId) {
    return ITEM_DOCUMENT_ROUTES.list.replace(':itemId', itemId);
}
/** Chemin concret d'un fichier rattaché à un article. */
export function itemDocumentPath(itemId, documentId) {
    return ITEM_DOCUMENT_ROUTES.detail.replace(':itemId', itemId).replace(':documentId', documentId);
}
/** Chemin concret de téléchargement d'un fichier rattaché à un article. */
export function itemDocumentDownloadPath(itemId, documentId) {
    return ITEM_DOCUMENT_ROUTES.download
        .replace(':itemId', itemId)
        .replace(':documentId', documentId);
}
/** Chemin concret d'une catégorie d'articles, à partir de son identifiant. */
export function itemCategoryPath(id) {
    return ITEM_CATEGORY_ROUTES.detail.replace(':id', id);
}
/** Chemin concret d'une unité de mesure, à partir de son identifiant. */
export function unitPath(id) {
    return UNIT_ROUTES.detail.replace(':id', id);
}
/** Chemin concret d'un taux de TVA, à partir de son identifiant. */
export function vatRatePath(id) {
    return VAT_RATE_ROUTES.detail.replace(':id', id);
}
/** Chemin concret d'un mode de règlement, à partir de son identifiant. */
export function paymentMethodPath(id) {
    return PAYMENT_METHOD_ROUTES.detail.replace(':id', id);
}
/** Chemin concret d'une condition de règlement, à partir de son identifiant. */
export function paymentTermPath(id) {
    return PAYMENT_TERM_ROUTES.detail.replace(':id', id);
}
/** Chemin concret d'un bloc de texte, à partir de son identifiant. */
export function textBlockPath(id) {
    return TEXT_BLOCK_ROUTES.detail.replace(':id', id);
}
/** Chemin concret d'un fournisseur, à partir de son identifiant. */
export function supplierPath(id) {
    return SUPPLIER_ROUTES.detail.replace(':id', id);
}
/** Chemin concret des conditions d'achat d'un article (liste), à partir de l'article. */
export function itemSuppliersPath(itemId) {
    return ITEM_SUPPLIER_ROUTES.list.replace(':itemId', itemId);
}
/** Chemin concret d'une condition d'achat d'article, à partir de l'article et de la ligne. */
export function itemSupplierPath(itemId, id) {
    return ITEM_SUPPLIER_ROUTES.detail.replace(':itemId', itemId).replace(':id', id);
}
/** Chemin concret d'une règle de numérotation, à partir de son identifiant. */
export function numberingSequencePath(id) {
    return NUMBERING_ROUTES.detail.replace(':id', id);
}
/** Chemin concret d'un devis, à partir de son identifiant. */
export function quotePath(id) {
    return QUOTE_ROUTES.detail.replace(':id', id);
}
/** Chemin concret d'évolution du statut d'un devis. */
export function quoteStatusPath(id) {
    return QUOTE_ROUTES.status.replace(':id', id);
}
/** Chemin concret du contexte legal public d'une societe. */
export function tenantPublicLegalPath(slug) {
    return TENANT_ROUTES.publicLegal.replace(':slug', slug);
}
export const API_HEADERS = {
    requestId: 'x-request-id',
    apiVersion: 'x-api-version',
    tenantId: 'x-tenant-id',
    idempotencyKey: 'idempotency-key',
    authorization: 'authorization',
};
export const WEB_ROUTES = {
    home: '/',
    login: '/connexion',
    register: '/inscription',
    invitation: '/invitation',
    /** Pages legales publiques adaptees a une societe via son slug. */
    companyPrivacy: '/societes/:slug/confidentialite',
    companyTerms: '/societes/:slug/conditions-utilisation',
    dashboard: '/tableau-de-bord',
    tiers: '/crm/tiers',
    prospects: '/crm/prospects',
    clients: '/crm/clients',
    /** Pipeline commercial et opportunités (document 07 § 7.14). */
    pipeline: '/crm/pipeline',
    /** Catalogue — articles (document 07 § 7.44). */
    catalogueItems: '/catalogue/articles',
    /** Catalogue — catégories d'articles. */
    catalogueCategories: '/catalogue/categories',
    /** Catalogue — référentiels (unités, TVA, modes et conditions de règlement). */
    catalogueSettings: '/catalogue/parametres',
    /** Catalogue — blocs de texte (E10). */
    catalogueTextBlocks: '/catalogue/blocs-texte',
    /** Catalogue — fournisseurs (E09-03). */
    catalogueSuppliers: '/catalogue/fournisseurs',
    /** Ventes — devis (E11). */
    quotes: '/ventes/devis',
    /** Rôles et permissions (document 07 § 7.30). */
    settingsRoles: '/parametres/roles',
    /** Paramètres de la société : chaîne documentaire, numérotation (document 09 § 11). */
    settingsCompany: '/parametres/societe',
    /** Paramètres des e-mails : signature et textes des messages sortants (document 09 § 11). */
    settingsEmail: '/parametres/emails',
    /** Utilisateurs et invitations (document 07 § 7.40). */
    settingsUsers: '/parametres/utilisateurs',
    /** Sécurité du compte : vérification en deux étapes (document 07 § 7.39). */
    settingsSecurity: '/parametres/securite',
};
/** Chemin public de la politique de confidentialite d'une societe. */
export function companyPrivacyPath(slug) {
    return WEB_ROUTES.companyPrivacy.replace(':slug', slug);
}
/** Chemin public des conditions d'utilisation d'une societe. */
export function companyTermsPath(slug) {
    return WEB_ROUTES.companyTerms.replace(':slug', slug);
}
//# sourceMappingURL=routes.constants.js.map